import React, { useState, useEffect } from 'react';
import {
  X,
  Building2,
  Briefcase,
  DollarSign,
  Calendar,
  Users,
  TrendingUp,
  CheckCircle,
  AlertCircle,
  FileText,
  ExternalLink,
  HelpCircle,
  ArrowRight,
  User,
  Phone,
  MapPin,
  Upload,
  Star,
  EyeOff
} from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

interface ApplicationQuestion {
  question: string;
}

interface JobPosting {
  id: string;
  job_title: string;
  requisition_number: string;
  job_description: string;
  skills_qualifications: string;
  worker_type: string;
  compensation_amount: number;
  comp_grade: string;
  hire_deadline_date: string;
  supervisory_organization: string;
  job_family: string;
  is_pro_posting?: boolean;
  company_name?: string;
  application_questions?: ApplicationQuestion[];
  background_image_url?: string | null;
  company_profile_image_url?: string | null;
  career_page_url?: string | null;
}

interface ConsultantProfile {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  title: string;
  country_region: string;
  experience_years: string;
  workday_modules: string;
  certifications: string;
  availability: string;
  hourly_rate: string;
  resume_url: string | null;
  photo_url: string | null;
}

interface ProfileField {
  label: string;
  key: keyof ConsultantProfile;
  icon: React.ReactNode;
}

interface JobDetailModalProps {
  job: JobPosting | null;
  isOpen: boolean;
  onClose: () => void;
  theme: string;
}

const REQUIRED_FIELDS: ProfileField[] = [
  { label: 'First Name', key: 'first_name', icon: <User className="w-4 h-4" /> },
  { label: 'Last Name', key: 'last_name', icon: <User className="w-4 h-4" /> },
  { label: 'Email', key: 'email', icon: <User className="w-4 h-4" /> },
  { label: 'Phone', key: 'phone', icon: <Phone className="w-4 h-4" /> },
  { label: 'Job Title', key: 'title', icon: <Briefcase className="w-4 h-4" /> },
  { label: 'Location', key: 'country_region', icon: <MapPin className="w-4 h-4" /> },
  { label: 'Years of Workday Experience', key: 'experience_years', icon: <TrendingUp className="w-4 h-4" /> },
  { label: 'Workday Modules', key: 'workday_modules', icon: <CheckCircle className="w-4 h-4" /> },
  { label: 'Resume', key: 'resume_url', icon: <Upload className="w-4 h-4" /> },
];

function getProfileCompleteness(p: ConsultantProfile): { pct: number; missing: ProfileField[] } {
  const missing = REQUIRED_FIELDS.filter(f => {
    const val = p[f.key];
    return !val || val.toString().trim() === '';
  });
  const filled = REQUIRED_FIELDS.length - missing.length;
  const pct = Math.round((filled / REQUIRED_FIELDS.length) * 100);
  return { pct, missing };
}

export default function JobDetailModal({ job, isOpen, onClose, theme }: JobDetailModalProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [applicationMode, setApplicationMode] = useState<'select' | 'profile'>('select');
  const [consultantProfile, setConsultantProfile] = useState<ConsultantProfile | null>(null);
  const [coverLetter, setCoverLetter] = useState('');
  const [questionAnswers, setQuestionAnswers] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [hasApplied, setHasApplied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [incompleteProfile, setIncompleteProfile] = useState<{ pct: number; missing: ProfileField[] } | null>(null);

  useEffect(() => {
    if (isOpen && job && user) {
      fetchConsultantProfile();
      checkExistingApplication();
    }
    if (isOpen && job) {
      const count = job.application_questions?.length || 0;
      setQuestionAnswers(Array(count).fill(''));
      setIncompleteProfile(null);
      setMessage(null);
      setApplicationMode('select');
    }
  }, [isOpen, job, user]);

  const fetchConsultantProfile = async () => {
    if (!user) return;
    try {
      const { data, error } = await supabase
        .from('consultant_profiles')
        .select('*')
        .eq('id', user.id)
        .maybeSingle();
      if (error) throw error;
      setConsultantProfile(data);
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkExistingApplication = async () => {
    if (!user || !job) return;
    try {
      const { data } = await supabase
        .from('job_applications')
        .select('id')
        .eq('job_posting_id', job.id)
        .eq('consultant_id', user.id)
        .maybeSingle();
      setHasApplied(!!data);
    } catch (error) {
      console.error('Error checking application:', error);
    }
  };

  const handleQuickApplyClick = () => {
    if (!consultantProfile) return;
    const result = getProfileCompleteness(consultantProfile);
    if (result.pct < 90) {
      setIncompleteProfile(result);
      return;
    }
    setIncompleteProfile(null);
    setApplicationMode('profile');
  };

  const handleQuickApply = async () => {
    if (!user || !job || !consultantProfile || !coverLetter.trim()) {
      setMessage({ type: 'error', text: 'Please provide why you are qualified and interested in this position.' });
      return;
    }

    const questions = job.application_questions || [];
    for (let i = 0; i < questions.length; i++) {
      if (!questionAnswers[i]?.trim()) {
        setMessage({ type: 'error', text: 'Please answer all application questions before submitting.' });
        return;
      }
    }

    try {
      setSubmitting(true);
      setMessage(null);

      const builtAnswers = questions.map((q, i) => ({
        question: q.question,
        answer: questionAnswers[i] || ''
      }));

      const { error } = await supabase
        .from('job_applications')
        .insert({
          job_posting_id: job.id,
          consultant_id: user.id,
          first_name: consultantProfile.first_name,
          last_name: consultantProfile.last_name,
          email: consultantProfile.email,
          phone: consultantProfile.phone,
          title: consultantProfile.title,
          country_region: consultantProfile.country_region,
          experience_years: consultantProfile.experience_years,
          workday_modules: consultantProfile.workday_modules,
          certifications: consultantProfile.certifications,
          availability: consultantProfile.availability,
          hourly_rate: consultantProfile.hourly_rate,
          message: coverLetter,
          cover_letter: coverLetter,
          resume_url: consultantProfile.resume_url,
          status: 'pending',
          question_answers: builtAnswers
        });

      if (error) throw error;

      setMessage({ type: 'success', text: 'Application submitted successfully!' });
      setHasApplied(true);
      setTimeout(() => {
        onClose();
        setCoverLetter('');
        setQuestionAnswers([]);
        setApplicationMode('select');
      }, 2000);
    } catch (error: any) {
      console.error('Error submitting application:', error);
      setMessage({
        type: 'error',
        text: error.message?.includes('duplicate')
          ? 'You have already applied to this position.'
          : 'Failed to submit application. Please try again.'
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen || !job) return null;

  const isDark = theme === 'dark';

  return (
    <div
      className="fixed inset-0 z-[200] overflow-y-auto bg-black/60"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="min-h-full flex items-start justify-center p-4 py-10">
        <div
          className={`relative w-full max-w-4xl rounded-xl shadow-2xl overflow-hidden ${
            isDark ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Branding header — cover photo + logo */}
          {(job.background_image_url || job.company_profile_image_url) && (
            <div className="relative">
              <div className="w-full overflow-hidden" style={{ paddingTop: 'calc(191 / 1584 * 100%)', position: 'relative' }}>
                {job.background_image_url ? (
                  <img src={job.background_image_url} alt="" className="absolute inset-0 w-full h-full object-cover" />
                ) : (
                  <div className={`absolute inset-0 ${isDark ? 'bg-gradient-to-r from-gray-700 to-gray-600' : 'bg-gradient-to-r from-slate-200 to-slate-300'}`} />
                )}
              </div>
              {job.company_profile_image_url && (
                <div className="absolute left-6 bottom-0 translate-y-1/2 z-10">
                  <div className={`w-16 h-16 rounded-xl border-4 overflow-hidden shadow-lg ${isDark ? 'border-gray-800 bg-gray-700' : 'border-white bg-gray-100'}`}>
                    <img src={job.company_profile_image_url} alt="Company logo" className="w-full h-full object-cover" />
                  </div>
                </div>
              )}
              {/* Close button over cover */}
              <button
                onClick={onClose}
                className="absolute top-3 right-3 p-2 rounded-lg bg-black/50 text-white hover:bg-black/70 transition-colors z-10"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          )}

          <div className={`flex items-center justify-between border-b ${
            (job.background_image_url || job.company_profile_image_url) ? 'px-6 pt-10 pb-4' : 'p-6'
          } ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
            <h2 className="text-2xl font-bold">{job.job_title || 'Job Details'}</h2>
            {!(job.background_image_url || job.company_profile_image_url) && (
              <button
                onClick={onClose}
                className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
              >
                <X className="w-6 h-6" />
              </button>
            )}
          </div>

          <div className="p-6 space-y-6">
            {hasApplied && (
              <div className="flex items-center gap-3 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <p className="text-green-800 dark:text-green-200 font-medium">You have already applied to this position</p>
              </div>
            )}

            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-primary" />
                {job.is_pro_posting && job.company_name ? (
                  <>
                    <span className="text-sm text-gray-500 dark:text-gray-400">Company:</span>
                    <span className="font-semibold">{job.company_name}</span>
                    <span className="flex items-center gap-1 px-2 py-0.5 bg-primary/20 text-primary text-xs font-bold rounded-full">
                      <Star className="w-3 h-3" />Pro
                    </span>
                  </>
                ) : (
                  <span className="flex items-center gap-1.5 text-sm italic text-gray-400">
                    <EyeOff className="w-4 h-4" />Company name hidden
                  </span>
                )}
              </div>
              {job.requisition_number && (
                <div className="flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-primary" />
                  <span className="text-sm">Req #: {job.requisition_number}</span>
                </div>
              )}
              <div className="bg-primary text-black px-3 py-1 rounded-full text-sm font-semibold capitalize">
                {job.worker_type || 'Contract'}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {job.job_family && (
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-primary" />
                  <div>
                    <p className="text-sm text-gray-500">Job Family</p>
                    <p className="font-medium">{job.job_family}</p>
                  </div>
                </div>
              )}
              {job.compensation_amount && (
                <div className="flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-primary" />
                  <div>
                    <p className="text-sm text-gray-500">Compensation</p>
                    <p className="font-medium">${job.compensation_amount.toLocaleString()}/year</p>
                  </div>
                </div>
              )}
              {job.comp_grade && (
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-primary" />
                  <div>
                    <p className="text-sm text-gray-500">Grade</p>
                    <p className="font-medium">{job.comp_grade}</p>
                  </div>
                </div>
              )}
              {job.hire_deadline_date && (
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-primary" />
                  <div>
                    <p className="text-sm text-gray-500">Application Deadline</p>
                    <p className="font-medium">{new Date(job.hire_deadline_date).toLocaleDateString()}</p>
                  </div>
                </div>
              )}
            </div>

            <div>
              <h3 className="text-lg font-bold mb-2">Job Description</h3>
              <p className={`whitespace-pre-wrap ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                {job.job_description}
              </p>
            </div>

            {job.skills_qualifications && (
              <div>
                <h3 className="text-lg font-bold mb-2">Skills & Qualifications</h3>
                <p className={`whitespace-pre-wrap ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                  {job.skills_qualifications}
                </p>
              </div>
            )}

            {/* Quick-post: direct Workday link */}
            {job.career_page_url && (
              <div className={`flex items-start gap-4 p-5 rounded-xl border ${isDark ? 'border-teal-800 bg-teal-900/20' : 'border-teal-200 bg-teal-50'}`}>
                <ExternalLink className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-semibold mb-1 ${isDark ? 'text-teal-300' : 'text-teal-800'}`}>Apply Directly via Workday</p>
                  <p className={`text-sm mb-3 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    This posting uses the company's own Workday portal. Click below to go directly to the requisition and apply there.
                  </p>
                  <a
                    href={job.career_page_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-lg transition-colors text-sm"
                  >
                    <ExternalLink className="w-4 h-4" />Open Workday Requisition
                  </a>
                </div>
              </div>
            )}

            {!hasApplied && (
              <div className={`border-t pt-6 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                <h3 className="text-xl font-bold mb-4">{job.career_page_url ? 'Also Apply via PerplexityATS' : 'Apply for this Position'}</h3>

                {incompleteProfile && (
                  <div className={`rounded-xl border p-5 mb-4 ${
                    isDark
                      ? 'bg-amber-900/20 border-amber-700'
                      : 'bg-amber-50 border-amber-200'
                  }`}>
                    <div className="flex items-start gap-3 mb-4">
                      <AlertCircle className="w-6 h-6 text-amber-600 shrink-0 mt-0.5" />
                      <div>
                        <p className={`font-semibold text-base ${isDark ? 'text-amber-300' : 'text-amber-800'}`}>
                          Your profile is {incompleteProfile.pct}% complete
                        </p>
                        <p className={`text-sm mt-1 ${isDark ? 'text-amber-400' : 'text-amber-700'}`}>
                          Complete a few more steps to unlock Quick Apply and start sending applications.
                        </p>
                      </div>
                    </div>

                    <p className={`text-sm font-medium mb-3 ${isDark ? 'text-amber-300' : 'text-amber-800'}`}>
                      Missing information:
                    </p>
                    <ul className="space-y-2 mb-5">
                      {incompleteProfile.missing.map((field) => (
                        <li key={field.key} className="flex items-center gap-2">
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                            isDark ? 'border-amber-500' : 'border-amber-400'
                          }`}>
                            <div className={`w-2 h-2 rounded-full ${isDark ? 'bg-amber-500' : 'bg-amber-400'}`} />
                          </div>
                          <span className={`text-sm ${isDark ? 'text-amber-300' : 'text-amber-700'}`}>
                            {field.label}
                          </span>
                        </li>
                      ))}
                    </ul>

                    <button
                      onClick={() => { onClose(); navigate('/consultant/profile'); }}
                      className="flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white font-semibold px-5 py-2.5 rounded-lg transition-colors text-sm"
                    >
                      Complete My Profile
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                )}

                {!incompleteProfile && applicationMode === 'select' && consultantProfile && (
                  <div className="space-y-4">
                    <p className={isDark ? 'text-gray-300' : 'text-gray-600'}>
                      Choose how you would like to apply:
                    </p>
                    <button
                      onClick={handleQuickApplyClick}
                      className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                        isDark
                          ? 'border-gray-700 hover:border-primary bg-gray-700'
                          : 'border-gray-200 hover:border-primary bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <CheckCircle className="w-6 h-6 text-primary" />
                        <div>
                          <p className="font-semibold">Quick Apply with Profile</p>
                          <p className="text-sm text-gray-500">Use your existing consultant profile information</p>
                        </div>
                      </div>
                    </button>
                  </div>
                )}

                {incompleteProfile && consultantProfile && (
                  <div className="space-y-4">
                    <button
                      onClick={handleQuickApplyClick}
                      className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                        isDark
                          ? 'border-gray-700 hover:border-primary bg-gray-700'
                          : 'border-gray-200 hover:border-primary bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <CheckCircle className="w-6 h-6 text-gray-400" />
                        <div>
                          <p className="font-semibold">Quick Apply with Profile</p>
                          <p className="text-sm text-gray-500">Complete your profile to unlock this option</p>
                        </div>
                      </div>
                    </button>
                  </div>
                )}

                {applicationMode === 'profile' && (
                  <div className="space-y-6">
                    <button
                      onClick={() => setApplicationMode('select')}
                      className="text-primary hover:underline text-sm"
                    >
                      Back to options
                    </button>

                    <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
                      <h4 className="font-semibold mb-3">Your Profile Information</h4>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <span className="text-gray-500">Name:</span> {consultantProfile?.first_name} {consultantProfile?.last_name}
                        </div>
                        <div>
                          <span className="text-gray-500">Email:</span> {consultantProfile?.email}
                        </div>
                        <div>
                          <span className="text-gray-500">Title:</span> {consultantProfile?.title}
                        </div>
                        <div>
                          <span className="text-gray-500">Workday Experience:</span> {consultantProfile?.experience_years}
                        </div>
                        <div className="col-span-2">
                          <span className="text-gray-500">Workday Modules:</span> {consultantProfile?.workday_modules}
                        </div>
                      </div>

                      {consultantProfile?.resume_url ? (
                        <div className="mt-4 pt-4 border-t border-gray-300 dark:border-gray-600">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <FileText className="w-5 h-5 text-primary" />
                              <span className="font-medium">Resume attached</span>
                            </div>
                            <a
                              href={consultantProfile.resume_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-1 text-primary hover:underline text-sm"
                            >
                              View Resume
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            This resume will be submitted with your application
                          </p>
                        </div>
                      ) : (
                        <div className="mt-4 pt-4 border-t border-gray-300 dark:border-gray-600">
                          <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
                            <AlertCircle className="w-5 h-5" />
                            <span className="text-sm font-medium">No resume uploaded</span>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            Consider uploading a resume to your profile for better application success
                          </p>
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Why are you the best fit for this position? <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        value={coverLetter}
                        onChange={(e) => setCoverLetter(e.target.value)}
                        placeholder="Share your relevant experience, skills, and why you're interested in this opportunity..."
                        rows={5}
                        className={`w-full px-4 py-3 rounded-lg border ${
                          isDark
                            ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                            : 'bg-white border-gray-300 text-gray-900'
                        } focus:outline-none focus:ring-2 focus:ring-primary`}
                        required
                      />
                    </div>

                    {job.application_questions && job.application_questions.length > 0 && (
                      <div>
                        <div className="flex items-center gap-2 mb-4">
                          <HelpCircle className="w-5 h-5 text-primary" />
                          <h4 className="font-semibold">Application Questions</h4>
                          <span className="text-sm text-gray-500">- All fields required</span>
                        </div>
                        <div className="space-y-4">
                          {job.application_questions.map((q, index) => (
                            <div key={index}>
                              <label className="block text-sm font-medium mb-2">
                                {index + 1}. {q.question} <span className="text-red-500">*</span>
                              </label>
                              <textarea
                                value={questionAnswers[index] || ''}
                                onChange={(e) => {
                                  const updated = [...questionAnswers];
                                  updated[index] = e.target.value;
                                  setQuestionAnswers(updated);
                                }}
                                placeholder="Your answer..."
                                rows={3}
                                className={`w-full px-4 py-3 rounded-lg border ${
                                  isDark
                                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                                    : 'bg-white border-gray-300 text-gray-900'
                                } focus:outline-none focus:ring-2 focus:ring-primary`}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {message && (
                      <div className={`flex items-center gap-3 p-4 rounded-lg ${
                        message.type === 'success'
                          ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
                          : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
                      }`}>
                        {message.type === 'success' ? (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        ) : (
                          <AlertCircle className="w-5 h-5 text-red-600" />
                        )}
                        <p className={message.type === 'success' ? 'text-green-800 dark:text-green-200' : 'text-red-800 dark:text-red-200'}>
                          {message.text}
                        </p>
                      </div>
                    )}

                    <div className="flex gap-3">
                      <button
                        onClick={handleQuickApply}
                        disabled={submitting || !coverLetter.trim()}
                        className="flex-1 bg-primary text-black font-semibold py-3 rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {submitting ? 'Submitting...' : 'Submit Application'}
                      </button>
                      <button
                        onClick={onClose}
                        className={`px-6 py-3 rounded-lg border ${
                          isDark
                            ? 'border-gray-600 hover:bg-gray-700'
                            : 'border-gray-300 hover:bg-gray-50'
                        } transition-colors`}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {!consultantProfile && !loading && (
                  <div className="text-center p-6 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                    <AlertCircle className="w-12 h-12 mx-auto mb-3 text-yellow-600" />
                    <p className="text-yellow-800 dark:text-yellow-200">
                      Please complete your consultant profile before applying to positions.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
